import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { IS_SSR } from '@/app/constants'
import type { CaptionTrack, Video } from '@/types/video'
import { findCueAt, isRtlCaption, parseWebVTT, selectCaptionTrack } from './webvtt'
import type { SubtitleCue } from './webvtt'

type UseVideoSubtitlesParams = {
  video?: Video | null
  /**
   * The player's media position, in seconds. A getter rather than the value, and a getter
   * rather than a player ref, so this hook stays free of `react-player` and the caller keeps
   * ownership of which player it is asking.
   */
  getCurrentTime: () => number | undefined
  /** False parks the cue loop — an off-screen feed card, or a closed fullscreen modal. */
  enabled?: boolean
}

type UseVideoSubtitlesResult = {
  /** The chosen track, or `null` when this viewer gets no captions. */
  track: CaptionTrack | null
  isRtl: boolean
  /** The cue being spoken, `null` between cues and when there are no captions at all. */
  text: string | null
}

const fetchCues = async (url: string): Promise<SubtitleCue[]> => {
  const response = await fetch(url)

  if (!response.ok) throw new Error(`Caption track ${url} answered ${response.status}`)

  return parseWebVTT(await response.text())
}

/**
 * The index of the cue playing right now, or `-1`.
 *
 * An effect, and a polled one, because media position is not observable any other way here.
 * `react-player`'s `onProgress` fires on a one-second interval — far too coarse for a
 * subtitle — and `components/Player/index.tsx` spreads `{...rest}` before assigning its own
 * `onProgress` on line 174, so a handler passed in from outside is discarded outright. There
 * is no store to subscribe to and no query to await: the number lives inside the player.
 *
 * The poll itself is free of re-renders. `findCueAt` gets the previous index as a hint, so a
 * frame costs two comparisons, and state is set only when the cue actually changes — a few
 * times a minute, inside the caption subtree alone.
 */
const useActiveCue = (
  cues: SubtitleCue[] | undefined,
  getCurrentTime: () => number | undefined,
  enabled: boolean
): number => {
  const [index, setIndex] = useState(-1)
  const indexRef = useRef(-1)

  // The caller passes an inline arrow, so its identity changes every render. Held in a ref
  // rather than listed as a dependency, which would tear down and restart the loop each time.
  const getCurrentTimeRef = useRef(getCurrentTime)
  getCurrentTimeRef.current = getCurrentTime

  useEffect(() => {
    const clear = () => {
      indexRef.current = -1
      setIndex(-1)
    }

    if (!enabled || !cues?.length) {
      clear()
      return
    }

    let frame = requestAnimationFrame(function tick() {
      const next = findCueAt(cues, getCurrentTimeRef.current() ?? 0, indexRef.current)

      if (next !== indexRef.current) {
        indexRef.current = next
        setIndex(next)
      }

      frame = requestAnimationFrame(tick)
    })

    return () => cancelAnimationFrame(frame)
  }, [cues, enabled])

  return index
}

/**
 * The subtitle cue to show over a video, in the viewer's language.
 *
 * Nothing here is discovered by the player: Bunny's HLS manifest declares
 * `CLOSED-CAPTIONS=NONE` and carries no caption rendition, so the track has to be chosen,
 * fetched and timed by hand.
 *
 * The language comes from `navigator.language`, which is the same signal
 * `app/api/middlewares/languageMiddleware.ts` already sends as `Accept-Language`. Using it
 * keeps the caption in the language the backend translated the surrounding lesson text into;
 * the `@codeleap/common` UI locale is a narrower axis (`en-GB` and `pt-BR` only) and would
 * silently disagree with what is on screen.
 *
 * Every way of having no captions — still encoding, unsupported language, English viewer, a
 * track that failed to download — ends in `text: null` and a video that keeps playing. None of
 * them is something a viewer can act on, so none of them surfaces an error.
 */
export const useVideoSubtitles = ({
  video,
  getCurrentTime,
  enabled = true
}: UseVideoSubtitlesParams): UseVideoSubtitlesResult => {
  const track = useMemo(() => selectCaptionTrack(video, IS_SSR ? null : navigator.language), [video])

  const { data: cues } = useQuery({
    // The URL already carries `?v=<version>`, so it changes whenever the track is
    // regenerated and is a sufficient key on its own.
    queryKey: ['subtitles', track?.url],
    queryFn: () => fetchCues(track.url),
    enabled: !!track?.url && enabled,
    // A `{url, version}` pair names an immutable file; there is nothing to revalidate.
    staleTime: Infinity,
    retry: 1
  })

  const index = useActiveCue(cues, getCurrentTime, enabled && !!track)

  return {
    track,
    isRtl: isRtlCaption(track),
    text: cues?.[index]?.text ?? null
  }
}
